import { Key, useEffect, useMemo, useRef, useState } from "react";
import Navigator from "../../component/navigator";
import styles from "./index.module.scss";
import iconCourse from "../../assets/tag_course.png";
import iconChallenge from "../../assets/tag_challenge.png";
import iconQuiz from "../../assets/tag_quiz.png";
import InlineProgress from "../../component/inline-progress";
import ChaptersNavigator from "../../component/chapters-navigator";
import { useNavigate, useParams } from "react-router-dom";
import useCourseOverview from "../../hooks/use-course-overview";
import PageEntry from "../../component/page-entry";
import { Numerical, Page } from "../../types";
import Tag from "../../component/tag";
import { QRCodeCanvas } from "qrcode.react";
import { Loading } from "@quark/design";
import Footer from "../../component/footer";
import ModalForWelcome from "../../component/modal-for-welcome";
import { getCertificationShareKey } from "../../api";
import classes from "classnames";
import CertificationModal from "../../component/certification-modal";
import useDocumentTitle from "../../hooks/use-document-title";
import dayjs from "dayjs";

const hasUndoPage = (
  pages: Omit<Page, "components">[]
): Numerical | undefined => {
  const firstUndoPage = pages.find((page) => {
    return page.status === "undo";
  });
  return firstUndoPage?.id;
};

const CourseCatalog = () => {
  const navigate = useNavigate();
  const snapshotRef = useRef<HTMLDivElement>(null);
  const [activeKey, setActiveKey] = useState<Key>("A");
  const [firstUndoId, setFirstUndoId] = useState<Numerical | undefined>();
  const { courseId } = useParams<{ courseId: string }>();
  const { overview, refresh } = useCourseOverview(courseId);
  const showFloatingQrcode = useMemo(() => {
    return overview?.isBuy === false;
  }, [overview]);

  useDocumentTitle(overview?.title + "-课程目录");

  const [showMallQrcode, setShowMallQrcode] = useState(false);

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const [showFreeVipModal, setShowFreeVipModal] = useState(false);

  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (!overview) {
      return;
    }
    if (overview.isBuy) {
      return;
    }
    const { progress } = overview;
    if (progress.learnPageNumber + progress.learnCoachNumber !== 0) {
      return;
    }
    const kWelcomeModal = "welcome-modal-" + overview.id;
    const lastShowTimestamp = window.localStorage.getItem(kWelcomeModal);
    const isToday = dayjs(parseInt(lastShowTimestamp || "0")).isToday();
    if (isToday) {
      return;
    }
    setShowWelcomeModal(true);
    window.localStorage.setItem(kWelcomeModal, Date.now().toString());
  }, [overview]);

  useEffect(() => {
    if (!courseId) {
      return;
    }
    if (
      [
        "015",
        "016",
        "017",
        "018",
        "019",
        "020",
        "021",
        "022",
        "023",
        "024",
        "025",
        "026",
        "027",
      ].includes(courseId)
    ) {
      return;
    }
    window.location.replace(
      `https://edu.py2fun.com/course/${courseId}/overview`
    );
  }, [courseId]);

  useEffect(() => {
    if (!firstUndoId) {
      return;
    }

    setTimeout(() => {
      document
        .querySelector("#page-" + firstUndoId)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
  }, [firstUndoId]);

  useEffect(() => {
    if (!overview) {
      return;
    }
    for (let i = 0; i < overview.categories.length; i++) {
      const category = overview.categories[i];
      const pageId = hasUndoPage(category.pages);
      if (pageId) {
        setActiveKey(category.id.toString());
        setFirstUndoId(pageId);
        break;
      }
    }
  }, [overview]);

  const pageProgress = useMemo<number>(() => {
    if (!overview) {
      return 0;
    }
    return Math.ceil(
      (overview.progress.learnPageNumber / overview.progress.totalPageNumber) *
        100
    );
  }, [overview]);

  const coachProgress = useMemo<number>(() => {
    if (!overview) {
      return 0;
    }
    if (overview.progress.totalCoachNumber === 0) {
      return 0;
    }
    return Math.ceil(
      (overview.progress.learnCoachNumber /
        overview.progress.totalCoachNumber) *
        100
    );
  }, [overview]);

  const handleActiveKeyChange = (key: Key) => {
    if (activeKey === key) {
      setActiveKey("");
    } else {
      setActiveKey(key);
    }
  };

  const handlePageCardClick = (page: Omit<Page, "components">) => {
    if (page.status === "lock" && !overview?.isBuy) {
      window._hmt.push(["_trackEvent", "course", "show_mall_qrcode", courseId]);
      setShowMallQrcode(true);
    }
  };

  const handleStartLearn = () => {
    if (!overview) {
      return;
    }
    const { categories } = overview;
    navigate("../page/" + categories[0].pages[0].id, {
      relative: "path",
    });
  };

  const handleShowCertification = () => {
    getCertificationShareKey(courseId!).then((shareKey) => {
      CertificationModal.show(shareKey);
    });
  };

  const renderWelcomeModal = () => {
    if (showWelcomeModal) {
      return (
        <ModalForWelcome
          onClose={() => setShowWelcomeModal(false)}
          onLearn={handleStartLearn}
        />
      );
    }
    return false;
  };

  const renderFreeVipModal = () => {
    if (!showFreeVipModal) {
      return null;
    }
    return (
      <div className={styles.modal}>
        <div className={styles.main}>
          <div
            className={styles.close}
            onClick={() => setShowFreeVipModal(false)}
          />
          <div className={styles.qrcodeWrapper}>
            <QRCodeCanvas
              value="https://mall.py2fun.com/buy/#/vip/open"
              size={168}
            />
          </div>
          <div className={styles.title}>开通会员畅享20+课程</div>
          <div className={styles.desc}>
            <span>每天不到一元钱</span>
            <br />
            <span>微信扫描二维码查看详情</span>
          </div>
        </div>
      </div>
    );
  };

  const renderShareModal = () => {
    if (!showShareModal) {
      return null;
    }
    return (
      <div className={styles.modal}>
        <div className={styles.main}>
          <div
            className={styles.close}
            onClick={() => setShowShareModal(false)}
          />
          <div className={styles.qrcodeWrapper}>
            <QRCodeCanvas
              value="https://edu.py2fun.com/mobile#/promotion"
              size={168}
            />
          </div>
          <div className={styles.title}>分享领取微信10元现金红包</div>
          <div className={styles.desc}>
            <span>微信扫描二维码</span>
            <br />
            <span>将课程分享给你的朋友吧！</span>
          </div>
        </div>
      </div>
    );
  };

  const handleShowVipModal = () => {
    navigate("/vip");
    window._hmt.push(["_trackEvent", "course", "redirect_to_vip", courseId]);
    setShowFreeVipModal(false);
  };

  const handleShowShareModal = () => {
    setShowShareModal(true);
    window._hmt.push(["_trackEvent", "course", "show_share_qrcode", courseId]);
  };

  if (!overview) {
    return <Loading fullscreen />;
  }

  return (
    <div ref={snapshotRef} className={styles.courseCatalog}>
      <Navigator activeKey="course" />
      <div className={styles.header}>
        <div className={styles.container}>
          <img className={styles.icon} src={overview.courseIcon} alt="" />
          <div className={styles.main}>
            <div className={styles.name}>{overview.title}</div>
            <div className={styles.tags}>
              <div>
                <Tag
                  icon={iconCourse}
                  value={overview.widgets.lesson}
                  text="小节"
                />
                <Tag
                  icon={iconQuiz}
                  value={overview.widgets.quiz}
                  text="测验"
                />
                <Tag
                  icon={iconChallenge}
                  value={overview.widgets.challenge}
                  text="挑战"
                />
              </div>
              <div>
                <button
                  onClick={handleShowVipModal}
                  className={classes(styles.button, styles.freeVipButton)}
                />
                <button
                  onClick={handleShowShareModal}
                  className={classes(styles.button, styles.shareButton)}
                />
              </div>
            </div>
            <div className={styles.descriptions}>{overview.summary}</div>
            <div
              className={classes(
                styles.progress,
                pageProgress === 100 && styles.finished
              )}
            >
              <InlineProgress
                className={styles.progressBar}
                label="学习进度"
                value={pageProgress}
              />
              <InlineProgress
                className={styles.progressBar}
                label="练习进度"
                value={coachProgress}
              />
              <button
                onClick={handleShowCertification}
                className={classes(styles.button, styles.certificateButton)}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.container}>
          <ChaptersNavigator.Accordion
            activeKey={activeKey}
            onChange={handleActiveKeyChange}
          >
            {overview.categories?.map((category) => {
              const [completed, total] = category.pages.reduce(
                ([completed, total], page) => {
                  total += 1;
                  if (page.status === "done") {
                    completed += 1;
                  }
                  return [completed, total];
                },
                [0, 0]
              );
              return (
                <ChaptersNavigator
                  className={styles.item}
                  icon={category.icon}
                  name={category.title}
                  completed={completed}
                  total={total}
                  key={category.id}
                >
                  {category.pages?.map((page) => {
                    return (
                      <PageEntry
                        page={page}
                        key={page.id}
                        onPageCardClick={handlePageCardClick}
                        onUpdate={() => {
                          refresh();
                        }}
                      />
                    );
                  })}
                  {category.coaches?.map((page) => {
                    return (
                      <PageEntry
                        fullWidth
                        type="coach"
                        page={page}
                        key={page.id}
                        onPageCardClick={handlePageCardClick}
                      />
                    );
                  })}
                </ChaptersNavigator>
              );
            })}
          </ChaptersNavigator.Accordion>
        </div>
      </div>
      {showFloatingQrcode && (
        <div className={styles.qrcode}>
          <QRCodeCanvas
            value={`https://mall.py2fun.com/mall/course/${courseId}?buy_from=learn`}
            className={styles.image}
            size={142}
          />
          <div className={styles.tips}>微信扫码购买</div>
        </div>
      )}
      {showMallQrcode && (
        <div className={styles.modal}>
          <div className={styles.main}>
            <div
              className={styles.close}
              onClick={() => setShowMallQrcode(false)}
            />
            <div className={styles.qrcodeWrapper}>
              <QRCodeCanvas
                value={`https://mall.py2fun.com/mall/course/${courseId}?buy_from=learn`}
                size={168}
              />
            </div>
            <div className={styles.title}>课程已锁定</div>
            <div className={styles.desc}>
              <span>微信扫描上方二维码解锁此课程</span>
              <br />
              <span>购课后刷新此页面即可</span>
            </div>
          </div>
        </div>
      )}
      {renderWelcomeModal()}
      {renderFreeVipModal()}
      {renderShareModal()}
      <Footer />
    </div>
  );
};

export default CourseCatalog;
